import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { LoadingPanel } from '../../components/ui/Loader';
import { ApiError, api } from '../../lib/api';

interface Claim {
  id: number;
  reference: string;
  status: 'actioned' | 'counter_filed';
  received_at: string;
  claimant_name: string;
  work_description: string;
  track: { id: number; title: string };
  counter_filed_at: string | null;
}

/**
 * Claims against your own tracks, and how to answer them.
 *
 * ## The half of takedown that platforms skip
 *
 * A notice-and-takedown system with no way to answer a bad claim is one where
 * any stranger with an email address can silence somebody's work permanently.
 * The counter-notice is a statutory right, and a right nobody can find is not
 * one - so this is a page, linked from the notification, rather than a support
 * address in a footer.
 *
 * ## It is honest about the cost
 *
 * Filing means giving your details to the claimant and agreeing to be sued
 * where they are. That is not a detail to bury under a submit button, because
 * somebody who files without knowing it has been badly served by the interface
 * that took their signature.
 */
export function ClaimsPage() {
  const { data, isPending } = useQuery({
    queryKey: ['my-claims'],
    queryFn: () => api.get<{ items: Claim[] }>('/me/dmca'),
  });

  return (
    <div className="mx-auto max-w-2xl px-6 py-12">
      <h1 className="text-3xl font-bold tracking-tight text-ink">Copyright claims</h1>
      <p className="mt-3 text-[15px] leading-relaxed text-soft">
        Claims made against your tracks, and what you can do about them.
      </p>

      {isPending ? (
        <LoadingPanel label="Checking" className="mt-8" />
      ) : (data?.items.length ?? 0) === 0 ? (
        <Card className="mt-8 p-8">
          <p className="text-sm text-soft">
            Nothing here — no claims have been made against anything of yours.
          </p>
        </Card>
      ) : (
        <ul className="mt-8 flex flex-col gap-4">
          {data?.items.map((claim) => (
            <li key={claim.id}>
              <ClaimCard claim={claim} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ClaimCard({ claim }: { claim: Claim }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [statement, setStatement] = useState('');
  const [signature, setSignature] = useState('');
  const [consent, setConsent] = useState(false);

  const file = useMutation({
    mutationFn: () =>
      api.post(`/me/dmca/${claim.id}/counter`, {
        statement: statement.trim(),
        signature: signature.trim(),
        consent_jurisdiction: consent,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['my-claims'] }),
  });

  const answered = claim.counter_filed_at !== null;

  return (
    <Card className="flex flex-col gap-4 p-5" data-claim={claim.id}>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <span className="font-mono text-[12px] text-ink">{claim.reference}</span>
        <span className={`meta-caps ${answered ? 'text-fresh' : 'text-hot-text'}`}>
          {answered ? 'you answered this' : 'taken down'}
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <Link to={`/tracks/${claim.track.id}`} className="text-[15px] font-medium text-ink hover:underline">
          {claim.track.title}
        </Link>
        <span className="font-mono text-[11px] text-soft">claimed by {claim.claimant_name}</span>
      </div>

      <div className="flex flex-col gap-1">
        <span className="meta-caps text-faint">What they say they own</span>
        <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-text-2">
          {claim.work_description}
        </p>
      </div>

      {answered ? (
        <p className="text-sm leading-relaxed text-soft">
          Your counter-notice has been filed and sent to the claimant. If they do not go to court,
          the track goes back up.
        </p>
      ) : !open ? (
        <div className="flex flex-col items-start gap-3 border-t border-line pt-4">
          <p className="text-sm leading-relaxed text-soft">
            If this claim is wrong — the recording is yours, or you have permission — you can file
            a counter-notice.
          </p>
          <Button variant="secondary" data-open-counter={claim.id} onClick={() => setOpen(true)}>
            Answer this claim
          </Button>
        </div>
      ) : (
        <form
          className="flex flex-col gap-4 border-t border-line pt-4"
          onSubmit={(event) => {
            event.preventDefault();
            file.mutate();
          }}
        >
          {/*
            Stated before the fields, not under the button. Filing hands your
            details to the claimant and agrees to be sued where they are, and
            somebody who signs without knowing that was failed by the form.
          */}
          <Alert tone="info">
            Filing sends your name and contact details to the claimant, and agrees that a court
            where they are may decide the matter. A knowingly false counter-notice carries the
            same liability as a false claim.
          </Alert>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-soft">Why the claim is wrong</span>
            <textarea
              required
              value={statement}
              maxLength={5000}
              data-counter-statement
              placeholder="Explain how you came to own or be licensed for this recording."
              onChange={(event) => setStatement(event.target.value)}
              className="min-h-32 resize-y rounded-[var(--radius-control)] border border-line bg-app px-3 py-2.5 text-sm text-ink placeholder:text-faint focus:border-line-strong"
            />
          </label>

          <label className="flex cursor-pointer items-start gap-3 rounded-[var(--radius-control)] border border-line p-3.5">
            <input
              type="checkbox"
              checked={consent}
              onChange={(event) => setConsent(event.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-signal)]"
            />
            <span className="text-[13px] leading-relaxed text-soft">
              I swear this is accurate, and I consent to the jurisdiction of a court where the
              claimant is located.
            </span>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-soft">Signature</span>
            <input
              required
              value={signature}
              maxLength={160}
              data-counter-signature
              placeholder="Your full legal name"
              onChange={(event) => setSignature(event.target.value)}
              className="rounded-[var(--radius-control)] border border-line bg-app px-3 py-2.5 text-sm text-ink placeholder:text-faint focus:border-line-strong"
            />
          </label>

          {file.isError && (
            <Alert tone="error">
              {file.error instanceof ApiError ? file.error.message : 'That could not be filed.'}
            </Alert>
          )}

          <div className="flex items-center gap-3">
            <Button type="submit" loading={file.isPending} disabled={!consent} data-file-counter>
              File the counter-notice
            </Button>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Not now
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}
